import "./App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";
import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import { PrivacyPolicy, Terms, DataDeletion } from "./pages/LegalPages";
import { IncentivesGuard } from "./components/IncentivePermissionList";
import Layout from "./components/Layout";
import ChunkErrorBoundary from "./components/ChunkErrorBoundary";

// Route-level code splitting: each page becomes its own chunk, loaded on demand.
// This keeps the initial JS bundle tiny so the app loads fast on every device.
const Login = lazy(() => import("./pages/Login"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Students = lazy(() => import("./pages/Students"));
const RecordedStudents = lazy(() => import("./pages/RecordedStudents"));
const MyDay = lazy(() => import("./pages/MyDay"));
const TeamPerformance = lazy(() => import("./pages/TeamPerformance"));
const StudentDetail = lazy(() => import("./pages/StudentDetail"));
const Enrollments = lazy(() => import("./pages/Enrollments"));
const CallReports = lazy(() => import("./pages/CallReports"));
const Leads = lazy(() => import("./pages/Leads"));
const Employees = lazy(() => import("./pages/Employees"));
const Franchisees = lazy(() => import("./pages/Franchisees"));
const Courses = lazy(() => import("./pages/Courses"));
const Incentives = lazy(() => import("./pages/Incentives"));
const Attendance = lazy(() => import("./pages/Attendance"));
const Integrations = lazy(() => import("./pages/Integrations"));
const Profile = lazy(() => import("./pages/Profile"));
const ImportZoho = lazy(() => import("./pages/ImportZoho"));
const Analytics = lazy(() => import("./pages/Analytics"));
const FranchiseeDetail = lazy(() => import("./pages/FranchiseeDetail"));
const FranchiseeAudit = lazy(() => import("./pages/FranchiseeAudit"));
const FranchiseeMeets = lazy(() => import("./pages/FranchiseeMeets"));
const FranchiseeTeams = lazy(() => import("./pages/FranchiseeTeams"));
const FranchiseeCockpit = lazy(() => import("./pages/FranchiseeCockpit"));
const WhatsappSending = lazy(() => import("./pages/WhatsappSending"));
const Mentorship = lazy(() => import("./pages/Mentorship"));
const FranchiseePerformance = lazy(() => import("./pages/FranchiseePerformance"));
const FranchiseeApply = lazy(() => import("./pages/FranchiseeApply"));
const CourseBatches = lazy(() => import("./pages/CourseBatches"));
const BatchDetail = lazy(() => import("./pages/BatchDetail"));
const MyPayslip = lazy(() => import("./pages/MyPayslip"));
const SeatHandover = lazy(() => import("./pages/SeatHandover"));
const Campaigns = lazy(() => import("./pages/Campaigns"));
const FacebookLeads = lazy(() => import("./pages/FacebookLeads"));
const LeadHub = lazy(() => import("./pages/LeadHub"));
const CallingQueue = lazy(() => import("./pages/CallingQueue"));
const CallingDashboard = lazy(() => import("./pages/CallingDashboard"));
const Reminders = lazy(() => import("./pages/Reminders"));
const PaymentFunnel = lazy(() => import("./pages/PaymentFunnel"));
const NeodoveIntegration = lazy(() => import("./pages/NeodoveIntegration"));
const EmployeeDetail = lazy(() => import("./pages/EmployeeDetail"));
const EmployeeDayReport = lazy(() => import("./pages/EmployeeDayReport"));
const OfficeIPSettings = lazy(() => import("./pages/OfficeIPSettings"));
const ChangePassword = lazy(() => import("./pages/ChangePassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Retarget = lazy(() => import("./pages/Retarget"));
const Drip = lazy(() => import("./pages/Drip"));
const StudentHealth = lazy(() => import("./pages/StudentHealth"));
const Tickets = lazy(() => import("./pages/Tickets"));
const DailyReport = lazy(() => import("./pages/DailyReport"));
const TeamReports = lazy(() => import("./pages/TeamReports"));
const TeamOverview = lazy(() => import("./pages/TeamOverview"));
const TeamReportDetail = lazy(() => import("./pages/TeamReportDetail"));
const WhatsappSenders = lazy(() => import("./pages/WhatsappSenders"));
const WhatsappInbox = lazy(() => import("./pages/WhatsappInbox"));
const Segments = lazy(() => import("./pages/Segments"));
const Schedules = lazy(() => import("./pages/Schedules"));
const EmailSettings = lazy(() => import("./pages/EmailSettings"));
const EmailTemplates = lazy(() => import("./pages/EmailTemplates"));
const EmailCenter = lazy(() => import("./pages/EmailCenter"));
const Tasks = lazy(() => import("./pages/Tasks"));
const Consultations = lazy(() => import("./pages/Consultations"));
const ConsultationDetail = lazy(() => import("./pages/ConsultationDetail"));
const AdRoiDashboard = lazy(() => import("./pages/AdRoiDashboard"));

const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[60vh] w-full" data-testid="page-loader">
    <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
  </div>
);

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster richColors position="top-right" />
          <ChunkErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/data-deletion" element={<DataDeletion />} />
              <Route path="/apply/franchisee" element={<FranchiseeApply />} />
              <Route
                path="/change-password"
                element={
                  <ProtectedRoute>
                    <ChangePassword />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Dashboard />} />
                <Route path="students" element={<Students />} />
                <Route path="recorded-students" element={<RecordedStudents />} />
                <Route path="my-day" element={<MyDay />} />
                <Route path="team-performance" element={<TeamPerformance />} />
                <Route path="students/:id" element={<StudentDetail />} />
                <Route path="enrollments" element={<Enrollments />} />
                <Route path="email" element={<EmailCenter />} />
                <Route path="leads" element={<Leads />} />
                <Route path="call-list" element={<Navigate to="/leads" replace />} />
                <Route path="retarget" element={<Retarget />} />
                <Route path="drip" element={<ProtectedRoute managerOrAdmin><Drip /></ProtectedRoute>} />
                <Route path="ad-roi" element={<ProtectedRoute managerOrAdmin><AdRoiDashboard /></ProtectedRoute>} />
                <Route path="student-health" element={<StudentHealth />} />
                <Route path="tickets" element={<Tickets />} />
                <Route path="courses" element={<Courses />} />
                <Route path="courses/:id/batches" element={<CourseBatches />} />
                <Route path="batches/:id" element={<BatchDetail />} />
                <Route path="my-incentives" element={<MyPayslip />} />
                <Route path="franchisees" element={<Franchisees />} />
                <Route path="franchisee-meets" element={<FranchiseeMeets />} />
                <Route path="franchisee-teams" element={<FranchiseeTeams />} />
                <Route path="franchisee-cockpit" element={<FranchiseeCockpit />} />
                <Route path="whatsapp-sending" element={<WhatsappSending />} />
                <Route path="mentorship" element={<Mentorship />} />
                <Route path="franchisee-performance" element={<FranchiseePerformance />} />
                <Route path="franchisees/audit" element={<ProtectedRoute adminOnly><FranchiseeAudit /></ProtectedRoute>} />
                <Route path="franchisees/:id" element={<FranchiseeDetail />} />
                <Route path="incentives" element={<IncentivesGuard><Incentives /></IncentivesGuard>} />
                <Route path="calling-queue" element={<CallingQueue />} />
                <Route path="campaigns" element={<ProtectedRoute adminOnly><Campaigns /></ProtectedRoute>} />
                <Route path="lead-hub" element={<ProtectedRoute managerOrAdmin><LeadHub /></ProtectedRoute>} />
                <Route path="facebook-leads" element={<ProtectedRoute adminOnly><FacebookLeads /></ProtectedRoute>} />
                <Route path="calling-dashboard" element={<ProtectedRoute managerOrAdmin><CallingDashboard /></ProtectedRoute>} />
                <Route path="seat-handovers/:id" element={<ProtectedRoute managerOrAdmin><SeatHandover /></ProtectedRoute>} />
                <Route path="attendance" element={<Attendance />} />
                <Route path="employees" element={<ProtectedRoute adminOnly><Employees /></ProtectedRoute>} />
                <Route path="employees/:id" element={<ProtectedRoute adminOnly><EmployeeDetail /></ProtectedRoute>} />
                <Route path="employees/:id/day-report" element={<ProtectedRoute adminOnly><EmployeeDayReport /></ProtectedRoute>} />
                <Route path="call-reports" element={<ProtectedRoute adminOnly><CallReports /></ProtectedRoute>} />
                <Route path="settings/office-ips" element={<ProtectedRoute adminOnly><OfficeIPSettings /></ProtectedRoute>} />
                <Route path="daily-report" element={<ProtectedRoute><DailyReport /></ProtectedRoute>} />
                <Route path="team-overview" element={<ProtectedRoute managerOrAdmin><TeamOverview /></ProtectedRoute>} />
                <Route path="team-reports" element={<ProtectedRoute adminOnly><TeamReports /></ProtectedRoute>} />
                <Route path="team-reports/:kind/:id" element={<ProtectedRoute><TeamReportDetail /></ProtectedRoute>} />
                <Route path="reminders" element={<ProtectedRoute adminOnly><Reminders /></ProtectedRoute>} />
                <Route path="integrations" element={<ProtectedRoute adminOnly><Integrations /></ProtectedRoute>} />
                <Route path="segments" element={<ProtectedRoute><Segments /></ProtectedRoute>} />
                <Route path="schedules" element={<ProtectedRoute><Schedules /></ProtectedRoute>} />
                <Route path="consultations" element={<ProtectedRoute><Consultations /></ProtectedRoute>} />
                <Route path="consultations/:id" element={<ProtectedRoute><ConsultationDetail /></ProtectedRoute>} />
                <Route path="tasks" element={<ProtectedRoute><Tasks /></ProtectedRoute>} />
                <Route path="email-templates" element={<ProtectedRoute adminOnly><EmailTemplates /></ProtectedRoute>} />
                <Route path="settings/email" element={<ProtectedRoute adminOnly><EmailSettings /></ProtectedRoute>} />
                <Route path="integrations/whatsapp" element={<ProtectedRoute adminOnly><WhatsappSenders /></ProtectedRoute>} />
                <Route path="whatsapp/inbox" element={<ProtectedRoute><WhatsappInbox /></ProtectedRoute>} />
                <Route path="integrations/neodove" element={<ProtectedRoute adminOnly><NeodoveIntegration /></ProtectedRoute>} />
                <Route path="import" element={<ProtectedRoute adminOnly><ImportZoho /></ProtectedRoute>} />
                <Route path="analytics" element={<ProtectedRoute adminOnly><Analytics /></ProtectedRoute>} />
                <Route path="analytics/payment-funnel" element={<ProtectedRoute><PaymentFunnel /></ProtectedRoute>} />
                <Route path="profile" element={<Profile />} />
              </Route>
              </Routes>
            </Suspense>
          </ChunkErrorBoundary>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
